import type { SpaceAddress } from "../../app-main/types/space.types";

import { BILLING_CHOICE } from "./AddSpaceAddressForm.consts";
import type { AddressFormValues } from "./AddSpaceAddressForm.schema";
import type { AddressPayload } from "./AddSpaceAddressForm.types";

/** Empty strings rather than `undefined`, so every input stays controlled. */
const buildDefaultValues = ({
  country,
  spaceAddress,
}: {
  country: string;
  spaceAddress: SpaceAddress;
}): AddressFormValues => ({
  country,
  spaceStreet: spaceAddress.street ?? "",
  spaceUnit: spaceAddress.unit ?? "",
  spaceCity: spaceAddress.city ?? "",
  spaceState: spaceAddress.state ?? "",
  spaceZip: spaceAddress.zip ?? "",
  billingChoice: BILLING_CHOICE.SameAsSpace,
  billingStreet: "",
  billingUnit: "",
  billingCity: "",
  billingState: "",
  billingZip: "",
});

/** Maps the space fields onto the keys the Spaces and Fees APIs expect. */
const buildSpaceAddressPayload = (
  values: AddressFormValues,
): AddressPayload => ({
  country: values.country,
  street: values.spaceStreet,
  unit: values.spaceUnit,
  city: values.spaceCity,
  state: values.spaceState,
  zip: values.spaceZip,
});

// Billing deliberately reuses `country`: a host can only list in one country, so the
// form renders a single country field for both addresses.
const buildBillingAddressPayload = (
  values: AddressFormValues,
): AddressPayload =>
  values.billingChoice === BILLING_CHOICE.SameAsSpace
    ? buildSpaceAddressPayload(values)
    : {
        country: values.country,
        street: values.billingStreet,
        unit: values.billingUnit,
        city: values.billingCity,
        state: values.billingState,
        zip: values.billingZip,
      };

/** Editing returns to the space; creating carries on to the next onboarding step. */
const buildNextStepUrl = ({
  isEdit,
  spaceId,
}: {
  isEdit: boolean;
  spaceId?: string;
}): string => `/your-space/${spaceId}${isEdit ? "" : "/setup"}`;

export {
  buildBillingAddressPayload,
  buildDefaultValues,
  buildNextStepUrl,
  buildSpaceAddressPayload,
};
